<script setup lang="ts">
/**
 * @link https://idsk.gov.sk/komponenty/formularove-prvky#form-select-boxes
 * @link https://github.com/id-sk/id-sk-frontend/tree/7070d5f8ada1dee22290fba2a7294a6cfd586f00/package/govuk/components/select
 */
import { computed, ref } from 'vue'
import { useField } from 'vee-validate'

export type IDSKSelectOptions = {
  [x: string]: string | number
}

export interface IDSKSelectProps
  extends Omit<HTMLSelectElement, 'options' | 'id' | 'disabled' | 'multiple'> {
  id?: string
  label: string
  readOnly?: boolean
  searchable?: boolean
  options: ReadonlyArray<IDSKSelectOptions>
  optionObjectValue?: string
  optionObjectLabel?: string
  resolveOnLoad?: boolean
  hint?: string
  hintId?: string
  /** Error message text, also serves as a flag whether the input is valid. */
  errorMessage?: string
  errorMessageId?: string
  modelValue: IDSKSelectOptions | string
  fullWidth?: boolean
  disabled?: boolean
  multiple?: boolean
  selectBoxText?: string
  emitPrimitive?: boolean
  showEmptyOption?: boolean
  createNewOptions?: boolean
  asObject?: boolean
  dataTestId?: string
}

const props = withDefaults(defineProps<IDSKSelectProps>(), {
  id: undefined,
  asObject: false,
  readOnly: false,
  optionObjectValue: 'id',
  optionObjectLabel: 'nazov',
  hint: undefined,
  hintId: undefined,
  errorMessage: undefined,
  errorMessageId: undefined,
  fullWidth: false,
  disabled: false,
  multiple: false,
  inputWidth: undefined,
  options: () => [],
  selectBoxText: undefined,
  emitPrimitive: false,
  showEmptyOption: false,
  createNewOptions: false,
  searchable: false,
  resolveOnLoad: false,
  dataTestId: '',
})

const emit = defineEmits(['update:modelValue', 'create-option'])

const currentSearch = ref()
const focusin = ref<boolean>(false)
const { setValue: setFormValue } = useField(props.id ?? 'undefined')

const value = computed(() => {
  return typeof props.modelValue === 'object'
    ? props.modelValue?.[props.optionObjectValue]?.toString()
    : props.modelValue
})

const handleMultiselectChange = (value: unknown) => {
  emit('update:modelValue', value)
}

/* const handleMultiselectStringChange = (value: unknown) => {
  console.log('VALUE MULTI', value)
  if (value) {
    emit('update:modelValue', value.nazov)
  }
} */

const createOption = (newOption: any) => {
  emit('create-option', newOption)
}

const changedNow = ref(false)

const changedVal = (val: any) => {
  changedNow.value = true
  setFormValue(val)
}

const changeFocus = (focus: Event) => {
  const classNames = Array.from((focus.target as HTMLElement).classList)
  if (classNames?.[0].includes('dropdown')) {
    return
  }
  if (focus.type === 'focusin' && changedNow.value) {
    focusin.value = true
  } else if (!changedNow.value && focus.type !== 'focusin') {
    focusin.value = false
    if (props.createNewOptions && currentSearch.value) {
      createOption(currentSearch.value)
      setFormValue(currentSearch.value)
    }
  }
}

const onSearchChange = (value: any) => {
  if (value !== '') {
    changedNow.value = false
    currentSearch.value = value
  }
}
</script>

<template>
  <div
    :class="[
      'govuk-form-group',
      {
        ['govuk-form-group--error']: errorMessage,
      },
    ]"
  >
    <IDSKFieldCaption
      :label="label"
      :for="props.id"
      :hint="hint"
      :hint-id="hintId"
      :error-message="errorMessage"
      :error-message-id="errorMessageId"
    />
    <p v-if="props.selectBoxText" class="govuk-body mb-0">
      {{ props.selectBoxText }}
    </p>
    <Multiselect
      v-if="props.multiple"
      :id="props.id"
      v-bind="$attrs"
      :model-value="modelValue"
      :disabled="props.disabled"
      :searchable="true"
      mode="tags"
      :value-prop="optionObjectValue"
      :label="optionObjectLabel"
      :track-by="optionObjectLabel"
      :close-on-select="false"
      :classes="{
        container: 'multiselect govuk-select',
      }"
      object
      :options="props.options"
      class="mb-6"
      no-options-text="Zoznam je prázdny"
      no-results-text="Nenašla sa zhoda"
      :data-test-id="
        props.id
          ? 'select-tags-search' + '-' + props.id
          : 'select-tags-search' + props.dataTestId
      "
      @update:model-value="handleMultiselectChange"
    />
    <Multiselect
      v-else-if="!props.createNewOptions && !props.multiple && props.searchable"
      :id="props.id"
      v-bind="$attrs"
      :model-value="modelValue"
      :disabled="props.disabled"
      :searchable="true"
      :min-chars="1"
      mode="single"
      :resolve-on-load="props.resolveOnLoad"
      :delay="100"
      :strict="false"
      :filterResults="false"
      :value-prop="optionObjectValue"
      :label="optionObjectLabel"
      :track-by="optionObjectLabel"
      :close-on-select="true"
      :classes="{
        container: 'multiselect govuk-select',
      }"
      :object="props.asObject"
      :options="props.options"
      class="mb-6"
      no-options-text="Zoznam je prázdny"
      no-results-text="Nenašla sa zhoda"
      :data-test-id="
        props.id ? 'select-search' + '-' + props.id : 'select-search' + props.dataTestId
      "
      @update:model-value="handleMultiselectChange"
    >
      <template v-for="(_, slot) in $slots" #[slot]="scope">
        <slot :name="slot" v-bind="scope || {}" />
      </template>
    </Multiselect>

    <Multiselect
      v-else-if="props.createNewOptions && !props.multiple"
      :id="props.id"
      style="padding: 0.7rem"
      v-bind="$attrs"
      :model-value="props.modelValue"
      :disabled="props.disabled"
      :searchable="true"
      mode="single"
      :value-prop="optionObjectValue"
      :label="optionObjectLabel"
      :track-by="optionObjectValue"
      :close-on-select="true"
      :classes="{
        container: 'multiselect govuk-select create-select',
      }"
      :object="false"
      :options="props.options"
      class="mb-6"
      no-options-text="Zoznam je prázdny"
      no-results-text="Nenašla sa zhoda"
      :data-test-id="
        props.id
          ? 'select-create-search' + '-' + props.id
          : 'select-create-search' + props.dataTestId
      "
      @update:model-value="handleMultiselectChange"
      @focusout="changeFocus"
      @focusin="changeFocus"
      @search-change="onSearchChange"
      @change="changedVal"
    />
    <select
      v-else
      :id="props.id"
      v-bind="$attrs"
      :disabled="props.disabled"
      :class="[
        'govuk-select',
        {
          'govuk-!-width-full': props.fullWidth,
          'govuk-select--multiple': props.multiple,
          'govuk-select--error': errorMessage,
        },
      ]"
      :value="value"
      :data-test-id="props.id ? 'select' + '-' + props.id : 'select' + props.dataTestId"
      @input="
        $emit(
          'update:modelValue',
          props.emitPrimitive
            ? options.find(
                (option) =>
                  option[props.optionObjectValue]?.toString() === $event.target.value
              )?.[props.optionObjectValue]
            : options.find(
                (option) =>
                  option[props.optionObjectValue]?.toString() === $event.target.value
              )
        )
      "
    >
      <option v-if="showEmptyOption && props.options?.length > 0" value="" />
      <option
        v-for="option in props.options"
        :key="option[props.optionObjectLabel]"
        :value="option[props.optionObjectValue]?.toString()"
      >
        {{ option[props.optionObjectLabel] }}
      </option>
    </select>
  </div>
</template>

<style lang="scss" scoped>
:deep(.multiselect) {
  --ms-px: 10px;
  --ms-py: 2px;
  --ms-caret-color: var(--idsk-color-black);
  --ms-radius: 0;
  --ms-border-color: var(--idsk-color-black);
  --ms-dropdown-border-color: var(--idsk-color-black);
  --ms-tag-bg: var(--idsk-color-dark-blue);
  --ms-tag-py: 2px;
  --ms-clear-color: var(--idsk-color-black);
}
:deep(.multiselect.govuk-select) {
  padding-top: 2px;
  padding-bottom: 2px;
}
:deep(.multiselect.govuk-select:focus-within) {
  outline: 3px solid #ffdf0f;
  outline-offset: 0;
  box-shadow: inset 0 0 0 2px;
}
</style>
